import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  MUIDataTable,
  MUIDataTableColumn,
  Typography,
  useDataTable,
} from 'web-library';
import {
  RegistrationRequestTab,
  SubjectList,
} from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import { ClassesAndTranslation } from '../../../models/ClassesAndTranslation';
import ViewCandidatesListDialog, {
  IViewCandidatesListDialogData,
} from './ViewCandidatesListDialog';
import { CandidateListBase } from '../../../api-refactored/group-exam-registration/model';
import { ComponentUsage } from './model';
import { useTabMutation } from './useTabMutation';

interface ISubjectDataColumns extends ClassesAndTranslation {
  dataContent: SubjectList[];
  setCandidatesListModalOpen: React.Dispatch<
    React.SetStateAction<{
      subjectName: string;
      subjectCode: string;
      candidateList: CandidateListBase[];
    }>
  >;
}

const subjectDataColumns = ({
  dataContent,
  t,
  classes,
  setCandidatesListModalOpen,
}: ISubjectDataColumns): MUIDataTableColumn[] => [
  {
    name: 'subjectName',
    label: t('exam->>SelectSubjects.SubjectName'),
    options: {
      setCellProps: () => ({ style: { width: '20%' } }),
      customBodyRenderLite: (dataIndex: number, rowIndex: number) => (
        <Typography variant="body2">
          {dataContent[dataIndex].subjectName}
          {'\u00A0'}({dataContent[dataIndex].subjectCode})
        </Typography>
      ),
    },
  },
  {
    name: 'subjectLevel',
    label: t('exam->>SelectSubjects.SubjectLevel'),
    options: {
      setCellProps: () => ({ style: { width: '10%' } }),
    },
  },
  {
    name: 'examSeries',
    label: t('exam->>SelectSubjects.ExamSeries'),
    options: {
      setCellProps: () => ({ style: { width: '20%' } }),
    },
  },
  {
    name: 'examLevel',
    label: t('exam->>SelectSubjects.ExamLevel'),
    options: {
      setCellProps: () => ({ style: { width: '20%' } }),
    },
  },
  {
    name: 'language',
    label: t('exam->>SelectSubjects.LanguageMedium'),
    options: {
      setCellProps: () => ({ style: { width: '20%' } }),
    },
  },
  {
    name: '',
    label: t('exam->>ReviewExam.NoOfCandidates'),
    options: {
      filter: false,
      sort: false,
      searchable: false,
      setCellProps: () => ({ style: { width: '30%', minWidth: '200px' } }),
      customBodyRenderLite: (dataIndex: number, rowIndex: number) => {
        const openViewDetailsModal = () => {
          setCandidatesListModalOpen({
            subjectName: dataContent[dataIndex].subjectName,
            subjectCode: dataContent[dataIndex].subjectCode,
            candidateList: dataContent[dataIndex].candidateList,
          });
        };
        return (
          <Box className={classes.actionButtonSmallLeft}>
            <Typography align="left" id={`text-examFees-row${rowIndex}`} variant="body2">
              {`${dataContent[dataIndex].candidateList.length}`} Candidates
            </Typography>
            <Button
              className={classes.noPaddingButton}
              id={`button-view-candidates-${dataIndex}`}
              variant="text"
              color="primary"
              size="small"
              onClick={openViewDetailsModal}
            >
              {t('exam->>ReviewExam.ViewCandidates')}
            </Button>
          </Box>
        );
      },
    },
  },
];

interface ISubjectListTab extends ClassesAndTranslation {
  applicationUuid: string;
  getCandidateOrSubjectType: RegistrationRequestTab;
  componentUsageFor: ComponentUsage;
}

const initialModalState = { subjectName: '', subjectCode: '', candidateList: [] };
const SubjectListTab = ({
  t,
  classes,
  applicationUuid,
  getCandidateOrSubjectType,
  componentUsageFor,
}: ISubjectListTab) => {
  const [
    candidatesListModalOpen,
    setCandidatesListModalOpen,
  ] = useState<IViewCandidatesListDialogData>(initialModalState);

  const { state: subjectTabTableState, dispatch: subjectTabTableDispatch } = useDataTable();
  const { payload, tableMutation } = useTabMutation({
    componentUsageFor,
    applicationUuid,
    dataTableHandler: {
      tabTableState: subjectTabTableState,
      tabTableDispatch: subjectTabTableDispatch,
    },
    registrationRequestTab: RegistrationRequestTab.SUBJECT_LIST,
  });

  useEffect(() => {
    // @ts-ignore
    tableMutation?.mutate(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subjectTabTableState.order,
    subjectTabTableState.page,
    subjectTabTableState.size,
    getCandidateOrSubjectType,
  ]);
  const closeCandidatesListModalHandler = () => {
    setCandidatesListModalOpen(initialModalState);
  };
  return (
    <>
      <ViewCandidatesListDialog
        data={candidatesListModalOpen}
        onClose={closeCandidatesListModalHandler}
      />
      <MUIDataTable
        columns={subjectDataColumns({
          dataContent: subjectTabTableState.data,
          t,
          classes,
          setCandidatesListModalOpen,
        })}
        data={subjectTabTableState.data}
        state={subjectTabTableState}
        dispatch={subjectTabTableDispatch}
        options={{
          pagination: true,
        }}
        rowsPerPageOptions={[5, 10, 20, 30, 50]}
      />
    </>
  );
};

export default SubjectListTab;
